// THE PEOPLE'S DEMANDS (Matt, after playtest 12, replacing the nightly
// wants): "the people yearn for something in particular. They want a
// specific technology. They want another warband. They want more houses...
// a sort of minor goal... if we succeed in it, we get some sort of a bonus.
// And if we fail in it, then we get some sort of a penalty."
//
// How it runs (game.js flipEvent):
// - Once the town is past DEMANDS.freeUpTo people, every so often the month's
//   card is THE PEOPLE SPEAK: two demands, from different roles, and the
//   table votes for one (the other is let go).
// - The chosen one is due on a hidden month within the next DEMANDS.window
//   months - never a fixed month - and on that month THE PEOPLE ASK turns
//   instead of a deck card, so nothing seeded later can bury it. Screens say
//   "sometime in the next N months", counting down, and never name a month
//   (Matt: "if it shows up in April, that's going to be like we lied").
// - It is judged at the flip: met, a glad town and a gift sized to the
//   effort; missed, an unhappy one and a grievance in the Hall's deck.
//
// "Achievable, but a couple of steps removed": a technology one prerequisite
// out of reach, a building whose technology is not known yet, a warband one
// bigger than any standing. Several need two roles at once. Pure; the
// judging reads only the civ and the mark taken when the demand was chosen.
import { TECHS, techReachable, branchOpen, techCost } from './cards';
import { BUILDINGS, count as cityCount } from './city';
import { housingOf } from './effects';
import { joinedToCity, roadLevel } from './roads';
import { shuffle } from './rng/rng';
import { VALUE } from './economy';

export const DEMANDS = {
  freeUpTo: 4, // a town this small wants for nothing (Matt: the first four are exempt)
  window: 3, // months in which a chosen demand will be asked
  gap: 4, // months after one is settled before the next is offered...
  gapPer: 4, // ...one fewer for every this many people past freeUpTo (down to 1)
  happiness: 2, // met
  missed: 1, // missed (and a grievance in the Hall's deck) - the bots revolted at 2
};

const has = (civ, id) => (civ.techs || []).includes(id);
const MACHINE_FOR_TIER = { 3: 'kiln', 4: 'still', 5: 'crucible' };
/** A technology the Scholars could reach with a step or two of work: its machine stands, if it needs one. */
const nearTech = (civ, id) => {
  const t = TECHS[id];
  if (!t || has(civ, id) || !branchOpen(civ, t.branch)) return false;
  if (t.tier >= 3 && cityCount(civ, MACHINE_FOR_TIER[t.tier]) < 1) return false;
  return true;
};
/** Missing prerequisites, each of which is reachable now: "one step removed". */
const oneStepAway = (civ, id) => nearTech(civ, id) && !techReachable(civ, id)
  && TECHS[id].needs.every((n) => has(civ, n) || techReachable(civ, n));
const warbands = (civ) => (civ.units || []).filter((u) => (u.kind || 'warband') === 'warband');

// Each kind: who it falls to, what it asks, how it is judged, and roughly
// what it takes in shifts (docs/ECONOMY.md), which sizes the gift.
const KINDS = {
  tech: {
    roles: ['science'],
    offers: (civ) => Object.keys(TECHS).filter((id) => oneStepAway(civ, id))
      .map((id) => ({ target: id, effort: (techCost(id) + TECHS[id].needs.filter((n) => !has(civ, n)).reduce((s, n) => s + techCost(n), 0)) * VALUE.knowledge })),
    label: (d) => `Learn ${TECHS[d.target].name}`,
    text: (d) => `People are asking for ${TECHS[d.target].name}. ${TECHS[d.target].text}`,
    met: (civ, d) => has(civ, d.target),
  },
  building: {
    roles: ['trade', 'science'],
    offers: (civ) => ['mill', 'warehouse', 'library', 'granary', 'market']
      .filter((type) => BUILDINGS[type])
      .filter((type) => {
        const need = BUILDINGS[type].needsTech;
        return !need ? cityCount(civ, type) < 2 : (!has(civ, need) && (techReachable(civ, need) || oneStepAway(civ, need)));
      })
      .map((type) => ({
        target: type,
        mark: cityCount(civ, type),
        effort: 2 + Object.entries(BUILDINGS[type].cost || {}).reduce((s, [k, v]) => s + v * (VALUE[k] ?? 0), 0)
          + (BUILDINGS[type].needsTech && !has(civ, BUILDINGS[type].needsTech) ? techCost(BUILDINGS[type].needsTech) : 0),
      })),
    label: (d) => (d.mark ? `Another ${BUILDINGS[d.target].name}` : `A ${BUILDINGS[d.target].name}`),
    text: (d) => `They want ${d.mark ? 'another' : 'a'} ${BUILDINGS[d.target].name.toLowerCase()} in the town.${BUILDINGS[d.target].needsTech ? ` It needs ${TECHS[BUILDINGS[d.target].needsTech].name} first.` : ''}`,
    met: (civ, d) => cityCount(civ, d.target) > (d.mark || 0),
  },
  homes: {
    roles: ['trade'],
    offers: (civ) => [{ target: 'homes', mark: housingOf(civ), effort: 6 }],
    label: () => 'Room to grow',
    text: (d) => `There is no room for new families. They want homes for ${d.mark + 2} people: another House.`,
    met: (civ, d) => housingOf(civ) >= d.mark + 2,
  },
  warband: {
    roles: ['military', 'trade'],
    offers: (civ) => {
      const biggest = Math.max(0, ...warbands(civ).map((u) => u.strength || 1));
      return [{ target: 'warband', mark: biggest + 1, effort: 3 + biggest * 3 }];
    },
    label: (d) => `A warband of ${d.mark}`,
    text: (d) => `The roads are not safe. They want a warband of ${d.mark} or more standing.`,
    met: (civ, d) => warbands(civ).some((u) => (u.strength || 1) >= d.mark),
  },
  village: {
    roles: ['military', 'trade'],
    offers: (civ) => [{ target: 'village', mark: (civ.villages || []).filter((v) => !v.taken).length, effort: 6 }],
    label: () => 'A new village',
    text: () => 'People want land of their own: one more village.',
    met: (civ, d) => (civ.villages || []).filter((v) => !v.taken).length > d.mark,
  },
  road: {
    roles: ['military', 'science'],
    offers: (civ) => {
      const cut = (civ.villages || []).filter((v) => !v.taken && !joinedToCity(civ, v.q, v.r));
      // Once Paths is known: a road is the Frontier's own work, a few hexes
      // of it (the bots never once laid one toward a village in time when
      // it also waited on the Scholars).
      if (!cut.length || roadLevel(civ) < 1) return [];
      return cut.map((v) => ({ target: v.id, name: v.name, effort: 4 }));
    },
    label: (d) => `A road to ${d.name}`,
    text: (d) => `${d.name} has no road to the city. They want one.`,
    met: (civ, d) => {
      const v = (civ.villages || []).find((x) => x.id === d.target);
      return Boolean(v) && joinedToCity(civ, v.q, v.r);
    },
  },
  camp: {
    roles: ['military'],
    offers: (civ) => ((civ.menaces || []).length ? [{ target: 'camp', mark: civ.cleared || 0, effort: 6 }] : []),
    label: () => 'Clear a camp',
    text: () => 'People are afraid of the camps. They want one of them gone.',
    met: (civ, d) => (civ.cleared || 0) > d.mark,
  },
  law: {
    roles: ['culture'],
    // Only once the Hall owns a law card to play: buying one AND playing it
    // inside the window was too far (the bots kept 11 of 129).
    offers: (civ) => {
      const h = civ.hall;
      const own = h && [...(h.deck || []), ...(h.hand || []), ...(h.discard || [])].some((id) => /^law_/.test(id));
      return own ? [{ target: 'law', mark: (h.laws || []).length, effort: 5 }] : [];
    },
    label: () => 'A law',
    text: () => 'People want a rule they can point to: a standing law.',
    met: (civ, d) => (civ.hall?.laws || []).length > d.mark,
  },
  council: {
    roles: ['culture'],
    offers: (civ) => (civ.hall && (civ.hall.council || []).length < 3 ? [{ target: 'council', effort: 3 }] : []),
    label: () => 'A full council',
    text: () => 'There are empty chairs in the Hall. They want every chair filled.',
    met: (civ) => (civ.hall?.council || []).length >= 3,
  },
  glad: {
    roles: ['culture', 'trade'],
    offers: (civ) => [{ target: 'glad', mark: Math.min(9, Math.max(7, (civ.happiness || 0) + 2)), effort: 4 }],
    label: (d) => `Happiness ${d.mark} when they ask`,
    text: (d) => `A hard season, and they want to feel it is behind them: happiness at ${d.mark} or more on the day they ask.`,
    met: (civ, d) => (civ.happiness || 0) >= d.mark,
  },
};

/** Is the town big enough to want anything? */
export const demandsAwake = (civ) => (civ.population || 0) > DEMANDS.freeUpTo;
/** Months between one demand settled and the next offered: fewer as the town grows. */
export const demandGap = (civ) => Math.max(1, DEMANDS.gap - Math.floor(Math.max(0, (civ.population || 0) - DEMANDS.freeUpTo) / DEMANDS.gapPer));

/**
 * Two demands to choose between, from different roles, each a couple of
 * steps away. Returns [options, rng]; an option is plain data (JSON-safe).
 */
export const offerDemands = (civ, rng) => {
  // A KIND first, then one of its options: buildings offer five types and
  // swamped the draw when every option went into one hat (the bots were
  // asked for a building in 238 of 623 demands, 2026-10-16).
  let kinds;
  let r = rng;
  [kinds, r] = shuffle(Object.keys(KINDS), r);
  const picked = [];
  for (const kind of kinds) {
    if (picked.length >= 2) break;
    const k = KINDS[kind];
    if (picked.some((p) => p.roles[0] === k.roles[0])) continue;
    const opts = k.offers(civ);
    if (!opts.length) continue;
    let one;
    [one, r] = shuffle(opts, r);
    picked.push({ kind, ...one[0], roles: k.roles });
  }
  return [picked.map((o) => ({ ...o, label: KINDS[o.kind].label(o), text: KINDS[o.kind].text(o) })), r];
};

/** Is it met? Judged at the flip. */
export const demandMet = (civ, d) => Boolean(d && KINDS[d.kind] && KINDS[d.kind].met(civ, d));

/** What meeting it gives: a glad town and coin, about half the effort back. */
export const demandReward = (d) => ({ happiness: DEMANDS.happiness, coin: Math.max(1, Math.round((d.effort || 4) / 2 / VALUE.coin / 2)) });
/** What missing it costs: an unhappy town (and a grievance, in game.js). */
export const demandPenalty = () => ({ happiness: -DEMANDS.missed });

/** "sometime in the next 3 months": honest as the months go by, never a date. */
export const demandWhen = (d, round) => {
  const left = Math.max(1, (d.windowEnd || round) - round + 1);
  return left === 1 ? 'this month or next' : `sometime in the next ${left} months`;
};
